import { useRef, useState } from "react";
import { Mic, MicOff, Sparkles, Plus, Trash2, Camera, Check, RefreshCw, ShieldCheck, AlertTriangle, FileText, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { fmtDate, fmtHours, fmtNOK, todayISO } from "@/lib/format";
import { DocStatus } from "@/components/drift/StatusBadge";
import { useSpeech } from "@/components/drift/useSpeech";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const Sub = ({ children, className }) => <h3 className={cn("font-display text-[15px] font-semibold tracking-tight", className)}>{children}</h3>;

/* ---------------- Report: note (text/voice) -> AI report ---------------- */
export function ReportSection({ order, onUpdated, big = false, readOnly = false }) {
  const [note, setNote] = useState(order.work_note || "");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const taRef = useRef(null);
  const speech = useSpeech({ onFinal: (t) => setNote((p) => (p ? `${p.trimEnd()} ${t}` : t)) });

  const generate = async () => {
    if (note.trim().length < 8) { toast.error("Skriv eller les inn litt meir om kva som vart gjort"); return; }
    if (speech.listening) speech.stop();
    setBusy(true);
    try {
      const r = await api.post(`/drift/orders/${order.id}/generate-report`, { note });
      onUpdated({ ...order, report: r.data, work_note: note });
      toast.success("Arbeidsrapport laga");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/drift/orders/${order.id}`, { report: draft });
      onUpdated({ ...order, report: r.data.report });
      setEditing(false);
      toast.success("Rapport lagra");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const rep = order.report;

  return (
    <div data-testid="report-section">
      {!rep || editing === "note" ? (
        <div>
          <div className="flex items-center justify-between">
            <Sub>{big ? "Kva vart gjort?" : "Arbeidsrapport"}</Sub>
            {speech.supported && !readOnly && (
              <button type="button" onClick={speech.toggle} data-testid="report-mic-button" aria-pressed={speech.listening}
                className={cn("inline-flex h-10 items-center gap-2 rounded-sm px-3 text-sm font-medium transition-colors", speech.listening ? "recording bg-brand text-white" : "bg-accent text-foreground hover:bg-accent/70")}>
                {speech.listening ? <><MicOff className="h-4 w-4" /> Stopp</> : <><Mic className="h-4 w-4" /> Snakk inn</>}
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Skriv eller snakk inn eit kort notat. Vi gjer det om til ein ryddig rapport til kunden.</p>
          <Textarea ref={taRef} rows={big ? 6 : 4} value={note} onChange={(e) => setNote(e.target.value)} disabled={readOnly}
            placeholder="T.d. «Feilsøkte kurs 12. Fann defekt termostat. Bytta termostat og testa varmekabel. Alt OK.»"
            className={cn("mt-3 rounded-sm", big && "text-base")} data-testid="report-note-input" />
          {speech.listening && <p className="mt-2 font-mono text-xs text-brand" data-testid="report-listening">● Lyttar … {speech.interim}</p>}
          {speech.error && <p className="mt-2 text-xs text-warn">{speech.error}</p>}
          {!speech.supported && !readOnly && <p className="mt-2 text-xs text-muted-foreground">Talegjenkjenning er ikkje tilgjengeleg i denne nettlesaren – skriv notatet.</p>}
          {!readOnly && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button onClick={generate} disabled={busy} className={cn("bg-brand text-white hover:bg-brand-dark", big && "h-12 flex-1 text-base")} data-testid="report-generate-button">
                {busy ? <><RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Lagar rapport …</> : <><Sparkles className="mr-2 h-4 w-4" /> Lag arbeidsrapport</>}
              </Button>
              {rep && <Button variant="ghost" onClick={() => setEditing(false)}>Avbryt</Button>}
            </div>
          )}
          {busy && <div className="mt-4 space-y-2" data-testid="report-generating"><Skeleton className="h-4 w-2/3 bg-accent" /><Skeleton className="h-4 w-full bg-accent" /><Skeleton className="h-4 w-5/6 bg-accent" /></div>}
        </div>
      ) : (
        <div data-testid="report-view">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Sub>{rep.tittel}</Sub>
              <p className="mt-0.5 text-xs text-muted-foreground">Arbeidsrapport {rep.generated_at ? `· ${fmtDate(rep.generated_at)}` : ""}</p>
            </div>
            {!readOnly && (
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => { setDraft({ ...rep, utfoert_arbeid: [...(rep.utfoert_arbeid || [])], avvik: [...(rep.avvik || [])] }); setEditing(true); }} data-testid="report-edit-button"><Pencil className="mr-1.5 h-3.5 w-3.5" /> Rediger</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing("note")} data-testid="report-redo-button"><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Lag på nytt</Button>
              </div>
            )}
          </div>
          {editing === true ? (
            <div className="mt-4 space-y-3" data-testid="report-edit-form">
              <Input value={draft.tittel} onChange={(e) => setDraft({ ...draft, tittel: e.target.value })} />
              <Textarea rows={5} value={(draft.utfoert_arbeid || []).join("\n")} onChange={(e) => setDraft({ ...draft, utfoert_arbeid: e.target.value.split("\n").filter(Boolean) })} placeholder="Eitt punkt per linje" />
              <Textarea rows={2} value={draft.funn || ""} onChange={(e) => setDraft({ ...draft, funn: e.target.value })} placeholder="Funn" />
              <Textarea rows={2} value={draft.maalingar_test || ""} onChange={(e) => setDraft({ ...draft, maalingar_test: e.target.value })} placeholder="Målingar / test" />
              <Textarea rows={2} value={(draft.avvik || []).join("\n")} onChange={(e) => setDraft({ ...draft, avvik: e.target.value.split("\n").filter(Boolean) })} placeholder="Avvik – eitt per linje" />
              <Textarea rows={2} value={draft.anbefaling || ""} onChange={(e) => setDraft({ ...draft, anbefaling: e.target.value })} placeholder="Anbefaling" />
              <Textarea rows={3} value={draft.kunde_tekst || ""} onChange={(e) => setDraft({ ...draft, kunde_tekst: e.target.value })} placeholder="Tekst til kunden" />
              <div className="flex gap-2"><Button onClick={saveEdit} disabled={busy} className="bg-brand text-white hover:bg-brand-dark" data-testid="report-save-button">Lagre</Button><Button variant="ghost" onClick={() => setEditing(false)}>Avbryt</Button></div>
            </div>
          ) : (
            <div className="mt-4 space-y-4 text-sm">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Utført arbeid</p>
                <ul className="mt-1.5 space-y-1">{(rep.utfoert_arbeid || []).map((s, i) => <li key={i} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-ok" />{s}</li>)}</ul>
              </div>
              {rep.funn && <div><p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Funn</p><p className="mt-1">{rep.funn}</p></div>}
              {rep.maalingar_test && <div><p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Målingar og test</p><p className="mt-1 font-mono text-[13px]">{rep.maalingar_test}</p></div>}
              {rep.avvik?.length > 0 && <div><p className="text-xs font-medium uppercase tracking-wider text-warn">Avvik</p><ul className="mt-1 space-y-1">{rep.avvik.map((s, i) => <li key={i} className="flex gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warn" />{s}</li>)}</ul></div>}
              {rep.anbefaling && <div><p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Anbefaling</p><p className="mt-1">{rep.anbefaling}</p></div>}
              <div className="rounded-sm border border-border bg-background p-3"><p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Til kunden</p><p className="mt-1 leading-relaxed">{rep.kunde_tekst}</p></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- Time entries ---------------- */
export function TimeSection({ order, onUpdated, big = false, readOnly = false, asUserId }) {
  const [hours, setHours] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const total = order.totals?.hours ?? order.time_entries.reduce((s, t) => s + Number(t.hours || 0), 0);

  const add = async (h) => {
    const val = Number(String(h ?? hours).replace(",", "."));
    if (!val || val <= 0) { toast.error("Skriv inn timar"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/drift/orders/${order.id}/time-entries`, { hours: val, description: desc, date: todayISO(), user_id: asUserId || undefined });
      const entries = [...order.time_entries, r.data];
      onUpdated({ ...order, time_entries: entries, status: ["ny", "planlagt"].includes(order.status) ? "paagaar" : order.status, totals: { ...order.totals, hours: entries.reduce((s, t) => s + Number(t.hours || 0), 0) } });
      setHours(""); setDesc("");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/drift/orders/${order.id}/time-entries/${id}`);
      const entries = order.time_entries.filter((t) => t.id !== id);
      onUpdated({ ...order, time_entries: entries, totals: { ...order.totals, hours: entries.reduce((s, t) => s + Number(t.hours || 0), 0) } });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="time-section">
      <div className="flex items-center justify-between"><Sub>Timar</Sub><span className="font-mono text-sm tabular" data-testid="time-total">{fmtHours(total)}</span></div>
      {order.time_entries.length > 0 && (
        <ul className="mt-3 divide-y divide-border rounded-sm border border-border">
          {order.time_entries.map((t) => (
            <li key={t.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid="time-entry-row">
              <span className="font-mono tabular">{fmtHours(t.hours)}</span>
              <span className="min-w-0 flex-1 truncate text-muted-foreground">{t.description || "Arbeid"}{t.user_name ? ` · ${t.user_name}` : ""} · {fmtDate(t.date, false)}</span>
              {!readOnly && <button onClick={() => remove(t.id)} className="text-muted-foreground hover:text-red-600 dark:hover:text-danger" aria-label="Slett" data-testid="time-entry-delete-button"><Trash2 className="h-4 w-4" /></button>}
            </li>
          ))}
        </ul>
      )}
      {!readOnly && (
        <div className="mt-3">
          <div className="flex gap-2">
            <Input type="number" step="0.25" min="0" inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="1,75" className={cn("w-28 rounded-sm font-mono", big && "h-12 text-lg")} data-testid="time-hours-input" />
            <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Kva (valfritt)" className={cn("flex-1 rounded-sm", big && "h-12")} data-testid="time-desc-input" />
            <Button onClick={() => add()} disabled={busy} variant="outline" className={cn("rounded-sm", big && "h-12")} data-testid="time-add-button"><Plus className="h-4 w-4" /></Button>
          </div>
          {big && <div className="mt-2 flex gap-2">{[0.5, 1, 1.5, 2].map((h) => <button key={h} onClick={() => add(h)} disabled={busy} className="h-10 flex-1 rounded-sm bg-accent font-mono text-sm hover:bg-accent/70" data-testid={`time-quick-${h}`}>+{String(h).replace(".", ",")} t</button>)}</div>}
        </div>
      )}
    </div>
  );
}

/* ---------------- Materials ---------------- */
export function MaterialSection({ order, onUpdated, big = false, readOnly = false }) {
  const [f, setF] = useState({ name: "", qty: "1", unit: "stk", unit_price: "" });
  const [busy, setBusy] = useState(false);
  const sum = order.materials.reduce((s, m) => s + Number(m.qty || 0) * Number(m.unit_price || 0), 0);

  const add = async () => {
    if (!f.name.trim()) { toast.error("Skriv inn materiell"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/drift/orders/${order.id}/materials`, { name: f.name, qty: Number(String(f.qty).replace(",", ".")) || 1, unit: f.unit || "stk", unit_price: Number(String(f.unit_price).replace(",", ".")) || 0 });
      onUpdated({ ...order, materials: [...order.materials, r.data] });
      setF({ name: "", qty: "1", unit: "stk", unit_price: "" });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/drift/orders/${order.id}/materials/${id}`); onUpdated({ ...order, materials: order.materials.filter((m) => m.id !== id) }); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="material-section">
      <div className="flex items-center justify-between"><Sub>Materiell</Sub>{sum > 0 && <span className="font-mono text-sm tabular">{fmtNOK(sum)}</span>}</div>
      {order.materials.length > 0 && (
        <ul className="mt-3 divide-y divide-border rounded-sm border border-border">
          {order.materials.map((m) => (
            <li key={m.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid="material-row">
              <span className="font-mono tabular">{m.qty} {m.unit}</span>
              <span className="min-w-0 flex-1 truncate">{m.name}</span>
              {m.unit_price > 0 && <span className="font-mono text-xs tabular text-muted-foreground">{fmtNOK(m.qty * m.unit_price)}</span>}
              {!readOnly && <button onClick={() => remove(m.id)} className="text-muted-foreground hover:text-red-600 dark:hover:text-danger" aria-label="Slett" data-testid="material-delete-button"><Trash2 className="h-4 w-4" /></button>}
            </li>
          ))}
        </ul>
      )}
      {!readOnly && (
        <div className="mt-3 grid grid-cols-[1fr_64px_auto] gap-2 sm:grid-cols-[1fr_70px_70px_100px_auto]">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="T.d. Termostat Microtemp" className={cn("rounded-sm", big && "h-12")} data-testid="material-name-input" />
          <Input value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} inputMode="decimal" className={cn("rounded-sm font-mono", big && "h-12")} data-testid="material-qty-input" />
          <Input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} className={cn("hidden rounded-sm sm:block", big && "h-12")} />
          <Input value={f.unit_price} onChange={(e) => setF({ ...f, unit_price: e.target.value })} inputMode="decimal" placeholder="Pris" className={cn("hidden rounded-sm font-mono sm:block", big && "h-12")} data-testid="material-price-input" />
          <Button onClick={add} disabled={busy} variant="outline" className={cn("rounded-sm", big && "h-12")} data-testid="material-add-button"><Plus className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
}

/* ---------------- Km ---------------- */
export function KmSection({ order, onUpdated, big = false, readOnly = false }) {
  const [km, setKm] = useState(order.km ?? 0);
  const save = async () => {
    const v = Number(String(km).replace(",", ".")) || 0;
    if (v === Number(order.km || 0)) return;
    try { const r = await api.patch(`/drift/orders/${order.id}`, { km: v }); onUpdated({ ...order, km: r.data.km, totals: r.data.totals }); toast.success("Km lagra"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="km-section">
      <Sub>Køyring</Sub>
      <div className="mt-3 flex items-center gap-2">
        <Input type="number" inputMode="decimal" min="0" value={km} onChange={(e) => setKm(e.target.value)} onBlur={save} disabled={readOnly} className={cn("w-28 rounded-sm font-mono", big && "h-12 text-lg")} data-testid="km-input" />
        <span className="text-sm text-muted-foreground">km</span>
      </div>
    </div>
  );
}

/* ---------------- Photos ---------------- */
export function PhotoSection({ order, onUpdated, big = false, readOnly = false }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const files = order.photo_files || [];

  const upload = async (list) => {
    const arr = Array.from(list || []).filter((f) => f.type.startsWith("image/"));
    if (!arr.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      arr.forEach((f) => fd.append("files", f));
      const r = await api.post(`/drift/orders/${order.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdated({ ...order, photos: [...order.photos, ...r.data.map((m) => m.id)], photo_files: [...files, ...r.data] });
      toast.success(`${r.data.length} bilete lasta opp`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (fid) => {
    try { await api.delete(`/drift/orders/${order.id}/photos/${fid}`); onUpdated({ ...order, photos: order.photos.filter((p) => p !== fid), photo_files: files.filter((f) => f.id !== fid) }); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="photo-section">
      <div className="flex items-center justify-between"><Sub>Bilete</Sub><span className="font-mono text-sm tabular text-muted-foreground">{files.length}</span></div>
      <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
        {files.map((f) => (
          <div key={f.id} className="group relative aspect-square overflow-hidden rounded-sm bg-accent" data-testid="photo-thumb">
            <a href={fileUrl(f.id)} target="_blank" rel="noreferrer"><img src={fileUrl(f.id)} alt={f.filename} className="h-full w-full object-cover" /></a>
            {!readOnly && <button onClick={() => remove(f.id)} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-sm bg-black/70 text-white opacity-0 transition-opacity group-hover:opacity-100" aria-label="Slett bilete" data-testid="photo-delete-button"><Trash2 className="h-3.5 w-3.5" /></button>}
          </div>
        ))}
        {!readOnly && (
          <button onClick={() => ref.current?.click()} disabled={busy} className={cn("flex aspect-square flex-col items-center justify-center gap-1.5 rounded-sm border border-dashed border-border text-muted-foreground transition-colors hover:border-foreground/50 hover:text-foreground")} data-testid="photo-add-button">
            <Camera className="h-5 w-5" /><span className="text-[11px]">{busy ? "Lastar …" : "Legg til"}</span>
          </button>
        )}
        <input ref={ref} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="photo-file-input" />
      </div>
    </div>
  );
}

/* ---------------- Checklist + AI doc check ---------------- */
const CHECKS = [["risikovurdering", "Risikovurdering (SJA) gjort"], ["sluttkontroll", "Sluttkontroll og målingar"], ["samsvarserklaering", "Samsvarserklæring levert"], ["kunde_informert", "Kunden informert"]];

export function ChecklistSection({ order, onUpdated, readOnly = false }) {
  const [busy, setBusy] = useState(false);
  const cl = order.checklist || {};
  const toggle = async (k, v) => {
    const next = { ...cl, [k]: v };
    onUpdated({ ...order, checklist: next });
    try { await api.patch(`/drift/orders/${order.id}`, { checklist: next }); } catch (e) { toast.error(errMsg(e)); }
  };
  const check = async () => {
    setBusy(true);
    try { const r = await api.post(`/drift/orders/${order.id}/check-docs`); onUpdated({ ...order, doc_check: r.data }); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const dc = order.doc_check;
  return (
    <div data-testid="checklist-section">
      <div className="flex items-center justify-between"><Sub>Dokumentasjon</Sub><DocStatus status={dc?.status} /></div>
      <ul className="mt-3 divide-y divide-border rounded-sm border border-border">
        {CHECKS.map(([k, l]) => (
          <li key={k} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
            <span>{l}</span>
            <Switch checked={!!cl[k]} onCheckedChange={(v) => toggle(k, v)} disabled={readOnly} data-testid={`checklist-${k}`} />
          </li>
        ))}
      </ul>
      <div className="mt-3 flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={check} disabled={busy} className="rounded-sm" data-testid="doc-check-button">{busy ? <><RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Kontrollerer …</> : <><ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Kontroller dokumentasjon</>}</Button>
      </div>
      {dc && (
        <div className={cn("mt-3 rounded-sm border p-3 text-sm", dc.status === "komplett" ? "border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/60" : dc.status === "manglar" ? "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/50" : "border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/50")} data-testid="doc-check-result">
          <p>{dc.oppsummering}</p>
          {dc.manglar?.length > 0 && <ul className="mt-2 space-y-1">{dc.manglar.map((m, i) => <li key={i} className="flex gap-2"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{m}</li>)}</ul>}
          {dc.merknader?.length > 0 && <ul className="mt-2 space-y-1 text-muted-foreground">{dc.merknader.map((m, i) => <li key={i} className="flex gap-2"><FileText className="mt-0.5 h-3.5 w-3.5 shrink-0" />{m}</li>)}</ul>}
        </div>
      )}
    </div>
  );
}

/* ---------------- Work brief (arbeidsbeskriving) ---------------- */
export function BriefSection({ order, onUpdated, compact = false }) {
  const [busy, setBusy] = useState(false);
  const b = order.brief;
  const gen = async () => {
    setBusy(true);
    try { const r = await api.post(`/drift/orders/${order.id}/brief`); onUpdated({ ...order, brief: r.data }); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="brief-section">
      <div className="flex items-center justify-between">
        <Sub>Arbeidsbeskriving</Sub>
        <Button size="sm" variant="ghost" onClick={gen} disabled={busy} className="text-xs" data-testid="brief-generate-button">{busy ? <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5 text-brand" />}{b ? "Oppdater" : "Førebu meg"}</Button>
      </div>
      {!b && !busy && <p className="mt-2 text-xs text-muted-foreground">Få ei kort sjekkliste basert på oppdraget, installasjonane og historikken på anlegget.</p>}
      {busy && <div className="mt-3 space-y-2"><Skeleton className="h-4 w-3/4 bg-accent" /><Skeleton className="h-4 w-full bg-accent" /><Skeleton className="h-4 w-2/3 bg-accent" /></div>}
      {b && !busy && (
        <div className="mt-3 space-y-3 text-sm" data-testid="brief-view">
          <p className="leading-relaxed">{b.oppsummering}</p>
          {[["Sjekk", b.sjekkliste], ["Ta med", b.ta_med], ["Hugs", b.hugs]].map(([t, arr]) => arr?.length > 0 && (
            <div key={t}><p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t}</p><ul className="mt-1 space-y-1">{arr.map((s, i) => <li key={i} className="flex gap-2"><span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />{s}</li>)}</ul></div>
          ))}
        </div>
      )}
    </div>
  );
}
